/**
 * SegmentsPage — `/segments`
 *
 * Listado de segmentos guardados de la marca. Mismo patrón que
 * ObjectivesListPage: KPI strip arriba, search, grid de cards, bloque
 * "Crear segmento" al final.
 *
 * El flow conversacional de creación vive en /segments/new.
 * El detalle de un segmento en /segments/:id.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Plus,
    Search,
    Users,
    Layers,
    TrendingUp,
    Filter,
    Loader2,
    AlertCircle,
    Wand2,
    ArrowRight,
    BarChart3,
    MoreVertical,
    Copy,
    Trash2,
    Send,
    Star,
    Calendar,
    Archive,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { ConfirmDangerModal } from "../../../shared/components/ui/ConfirmDangerModal";
import { toast } from "../../../shared/hooks/useToast";
import { segmentsApi } from "../api";
import type { Segment } from "../types";

const MOCK_BRAND_ID = "brand-demo";

export function SegmentsPage() {
    const navigate = useNavigate();
    const [segments, setSegments] = useState<Segment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<
        "all" | "saved" | "draft"
    >("all");

    useEffect(() => {
        (async () => {
            setLoading(true);
            const res = await segmentsApi.list(MOCK_BRAND_ID);
            if (res.success) setSegments(res.data);
            else setError(res.error || "Error al cargar segmentos");
            setLoading(false);
        })();
    }, []);

    /* ─── Filtros + búsqueda ─── */
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return segments.filter((s) => {
            if (statusFilter !== "all" && s.status !== statusFilter) return false;
            if (q) {
                const hay =
                    `${s.name || ""} ${s.conversationSummary || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [segments, search, statusFilter]);

    /* ─── KPIs ─── */
    const stats = useMemo(() => {
        const saved = segments.filter((s) => s.status === "saved");
        const totalReach = saved.reduce(
            (sum, s) => sum + (s.lastKnownCount || 0),
            0,
        );
        const largest = saved.reduce<Segment | null>(
            (max, s) =>
                !max || (s.lastKnownCount || 0) > (max.lastKnownCount || 0)
                    ? s
                    : max,
            null,
        );
        return {
            total: saved.length,
            drafts: segments.filter((s) => s.status === "draft").length,
            totalReach,
            largest,
        };
    }, [segments]);

    const [pendingDelete, setPendingDelete] = useState<{ id: string; name?: string } | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const handleDelete = (id: string, name?: string) => {
        setPendingDelete({ id, name });
    };
    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleteBusy(true);
        const res = await segmentsApi.delete(pendingDelete.id, MOCK_BRAND_ID);
        setDeleteBusy(false);
        if (res.success) {
            setSegments((prev) => prev.filter((s) => s.id !== pendingDelete.id));
            toast.success("Segmento eliminado");
        } else {
            toast.error("Error al eliminar el segmento");
        }
        setPendingDelete(null);
    };

    // Audit A-04: el kebab "Duplicar" no hacía nada. Ahora usa el endpoint real.
    const handleDuplicate = async (id: string) => {
        const res = await segmentsApi.duplicate(id, MOCK_BRAND_ID);
        if (res.success) {
            setSegments((prev) => [res.data, ...prev]);
            toast.success("Segmento duplicado");
        } else {
            toast.error(res.error || "No se pudo duplicar el segmento");
        }
    };

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-7xl space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                                Segmentos
                            </h1>
                            {!loading && (
                                <span className="px-2.5 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold">
                                    {stats.total}
                                </span>
                            )}
                        </div>
                        <p className="text-slate-500 text-lg">
                            Grupos de usuarios reutilizables — la base de toda
                            campaña y envío.
                        </p>
                    </div>
                    <Link to="/segments/new">
                        <Button variant="primary">
                            <Plus size={16} />
                            Nuevo segmento
                        </Button>
                    </Link>
                </div>

                {/* KPI strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                        label="Segmentos guardados"
                        value={stats.total.toString()}
                        icon={Layers}
                        iconColor="text-violet-600"
                        iconBg="bg-violet-100"
                    />
                    <StatCard
                        label="Alcance total"
                        value={stats.totalReach.toLocaleString("es-AR")}
                        sub="suma de usuarios únicos"
                        icon={Users}
                        iconColor="text-emerald-600"
                        iconBg="bg-emerald-100"
                    />
                    <StatCard
                        label="Más grande"
                        value={
                            stats.largest
                                ? (
                                      stats.largest.lastKnownCount || 0
                                  ).toLocaleString("es-AR")
                                : "—"
                        }
                        sub={stats.largest?.name || "Sin segmentos"}
                        icon={TrendingUp}
                        iconColor="text-blue-600"
                        iconBg="bg-blue-100"
                    />
                    <StatCard
                        label="Borradores"
                        value={stats.drafts.toString()}
                        sub="sin guardar todavía"
                        icon={Archive}
                        iconColor="text-amber-600"
                        iconBg="bg-amber-100"
                    />
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                        />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar por nombre o descripción..."
                            aria-label="Buscar segmentos por nombre o descripción"
                            className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm
                                focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
                        />
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Filter size={14} className="text-slate-400 flex-shrink-0" />
                        <FilterChip
                            label="Todos"
                            active={statusFilter === "all"}
                            onClick={() => setStatusFilter("all")}
                        />
                        <FilterChip
                            label="Guardados"
                            active={statusFilter === "saved"}
                            onClick={() => setStatusFilter("saved")}
                        />
                        <FilterChip
                            label="Borradores"
                            active={statusFilter === "draft"}
                            onClick={() => setStatusFilter("draft")}
                        />
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
                        <AlertCircle size={18} />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                )}

                {/* Grid / empty / loading */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm">
                        <Loader2
                            className="animate-spin text-violet-500 mb-4"
                            size={32}
                        />
                        <p className="text-slate-500">Cargando segmentos…</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        hasSegments={segments.length > 0}
                        onCreate={() => navigate("/segments/new")}
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {filtered.map((s) => (
                                <SegmentCard
                                    key={s.id}
                                    segment={s}
                                    onDelete={() => handleDelete(s.id, s.name)}
                                    onDuplicate={() => handleDuplicate(s.id)}
                                />
                            ))}
                        </div>

                        {/* Bloque al final: crear segmento */}
                        <Link
                            to="/segments/new"
                            className="block mt-4 relative overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-gradient-to-r from-slate-50 via-white to-violet-50/40 hover:border-violet-400 hover:from-violet-50/40 hover:via-white hover:to-fuchsia-50/40 transition-all duration-300 group"
                        >
                            <div className="absolute -right-12 -top-12 w-48 h-48 bg-gradient-to-br from-violet-200/40 to-fuchsia-200/30 rounded-full blur-3xl pointer-events-none group-hover:scale-110 transition-transform duration-500" />

                            <div className="relative flex items-center gap-4 px-6 py-5">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md shadow-violet-500/30 group-hover:scale-105 group-hover:rotate-3 transition-transform duration-300 flex-shrink-0">
                                    <Wand2 size={20} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-base font-bold text-slate-900">
                                            Crear segmento con IA
                                        </h3>
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                                            Lenguaje natural
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-600 mt-0.5">
                                        Describí tu audiencia en una frase ("clientes que
                                        no piden hace 60 días") y la IA arma el filtro.
                                    </p>
                                </div>
                                <div className="flex-shrink-0 bg-white border border-violet-200 text-violet-700 group-hover:bg-violet-600 group-hover:text-white group-hover:border-violet-600 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all duration-300 shadow-sm">
                                    Empezar
                                    <ArrowRight
                                        size={14}
                                        className="transition-transform group-hover:translate-x-0.5"
                                    />
                                </div>
                            </div>
                        </Link>
                    </>
                )}
            </div>

            {pendingDelete && (
                <ConfirmDangerModal
                    title="Borrar segmento"
                    description={`¿Borrar el segmento "${pendingDelete.name || "sin nombre"}"? Esta acción no se puede deshacer.`}
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDelete(null)}
                    busy={deleteBusy}
                />
            )}
        </div>
    );
}

/* ─── Sub-components ─── */

function SegmentCard({
    segment,
    onDelete,
    onDuplicate,
}: {
    segment: Segment;
    onDelete: () => void;
    onDuplicate: () => void;
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const isDraft = segment.status === "draft";
    const isFresh = segment.lastUsedAt
        ? Date.now() - new Date(segment.lastUsedAt).getTime() < 7 * 86400000
        : false;
    const emoji = (segment as any).emoji || "🎯";

    return (
        <div
            className={`group relative bg-white rounded-2xl border shadow-sm transition-all duration-300 overflow-hidden ${
                isDraft
                    ? "border-amber-200 bg-gradient-to-br from-amber-50/40 to-white"
                    : "border-slate-100 hover:border-slate-200 hover:shadow-md hover:-translate-y-0.5"
            }`}
        >
            <Link to={`/segments/${segment.id}`} className="block p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="text-3xl flex-shrink-0 leading-none">
                            {emoji}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                {isDraft ? (
                                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                        Borrador
                                    </span>
                                ) : (
                                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                        Guardado
                                    </span>
                                )}
                                {isFresh && (
                                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100 flex items-center gap-1">
                                        <Star size={9} fill="currentColor" />
                                        Activo
                                    </span>
                                )}
                            </div>
                            <h3 className="font-bold text-slate-900 leading-tight truncate">
                                {segment.name || "Segmento sin nombre"}
                            </h3>
                        </div>
                    </div>
                </div>

                {/* Summary */}
                <p className="text-xs text-slate-600 leading-relaxed line-clamp-2 mb-3 italic">
                    "{segment.conversationSummary || "Sin descripción"}"
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 bg-slate-50 rounded-xl p-3">
                    <div>
                        <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-0.5">
                            <Users size={10} />
                            Usuarios
                        </div>
                        <p className="text-xl font-bold text-slate-900 tabular-nums">
                            {(segment.lastKnownCount || 0).toLocaleString("es-AR")}
                        </p>
                    </div>
                    <div>
                        <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-0.5">
                            <Calendar size={10} />
                            Último uso
                        </div>
                        <p className="text-sm font-semibold text-slate-700">
                            {timeAgo((segment as any).lastUsedAt)}
                        </p>
                    </div>
                </div>
            </Link>

            {/* Action bar */}
            <div className="flex items-center justify-between gap-2 px-5 pb-4 relative z-10">
                <Link
                    to={`/segments/${segment.id}`}
                    className="group/btn flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] text-center transition-all duration-200 flex items-center justify-center gap-2 shadow-sm shadow-violet-500/20 hover:shadow-md hover:shadow-violet-500/30"
                >
                    <BarChart3 size={15} />
                    Ver detalle
                    <ArrowRight
                        size={14}
                        className="transition-transform group-hover/btn:translate-x-0.5"
                    />
                </Link>

                {/* Kebab */}
                <div className="relative">
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            setMenuOpen((o) => !o);
                        }}
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-white/80 transition-colors border border-slate-200/60 bg-white/60"
                    >
                        <MoreVertical size={16} />
                    </button>

                    {menuOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-30"
                                onClick={() => setMenuOpen(false)}
                            />
                            <div className="absolute right-0 bottom-full mb-1 w-44 bg-white rounded-xl border border-slate-200 shadow-lg z-40 overflow-hidden">
                                <Link
                                    to="/objectives/new"
                                    state={{ segmentId: segment.id, segmentName: segment.name }}
                                    onClick={() => setMenuOpen(false)}
                                    className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-violet-700 hover:bg-violet-50"
                                >
                                    <Send size={13} />
                                    Usar en campaña
                                </Link>
                                <button
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onDuplicate();
                                    }}
                                    className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-slate-700 hover:bg-slate-50"
                                >
                                    <Copy size={13} />
                                    Duplicar
                                </button>
                                <div className="border-t border-slate-100" />
                                <button
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onDelete();
                                    }}
                                    className="w-full px-3 py-2 text-left flex items-center gap-2 text-xs text-red-600 hover:bg-red-50"
                                >
                                    <Trash2 size={13} />
                                    Borrar
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

interface StatCardProps {
    label: string;
    value: string;
    sub?: string;
    icon: typeof Users;
    iconColor: string;
    iconBg: string;
}

function StatCard({
    label,
    value,
    sub,
    icon: Icon,
    iconColor,
    iconBg,
}: StatCardProps) {
    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-start gap-3">
            <div
                className={`w-10 h-10 rounded-xl ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0`}
            >
                <Icon size={18} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    {label}
                </p>
                <p className="text-xl font-bold text-slate-900 tabular-nums truncate">
                    {value}
                </p>
                {sub && (
                    <p className="text-[10px] text-slate-500 truncate mt-0.5">
                        {sub}
                    </p>
                )}
            </div>
        </div>
    );
}

function FilterChip({
    label,
    active,
    onClick,
}: {
    label: string;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className={`px-2.5 py-1.5 text-xs font-medium rounded-full transition-colors ${
                active
                    ? "bg-violet-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
        >
            {label}
        </button>
    );
}

function EmptyState({
    hasSegments,
    onCreate,
}: {
    hasSegments: boolean;
    onCreate: () => void;
}) {
    return (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm text-center">
            <div className="w-16 h-16 bg-violet-50 rounded-2xl flex items-center justify-center mb-4">
                <Layers size={28} className="text-violet-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-2">
                {hasSegments
                    ? "Ningún segmento coincide con los filtros"
                    : "Todavía no tenés segmentos"}
            </p>
            <p className="text-slate-500 text-sm mb-5 max-w-md">
                {hasSegments
                    ? "Ajustá los filtros o probá una búsqueda distinta."
                    : "Los segmentos son grupos de usuarios reutilizables. Crealos una vez y los usás en todas tus campañas."}
            </p>
            {!hasSegments && (
                <Button variant="primary" onClick={onCreate}>
                    <Wand2 size={14} />
                    Crear primer segmento con IA
                </Button>
            )}
        </div>
    );
}

function timeAgo(iso?: string): string {
    if (!iso) return "Nunca";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "ahora";
    if (diffMin < 60) return `hace ${diffMin} min`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `hace ${diffHr} h`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `hace ${diffDay} d`;
    return d.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
}

export default SegmentsPage;
